<template>
  <div
    v-if="isOpen"
    class="review-prompt__scrim"
    @click.self="dismiss"
  >
    <div
      ref="modal"
      class="review-prompt"
      role="dialog"
      aria-modal="true"
      :aria-label="heading"
      @keydown="onKeydown"
    >
      <!-- Yellow band persists across all three steps. The icon is inlined rather than an
           <img src>: the host can serve SVG as text/plain, and inlining is what lets
           currentColor take the dark fill on yellow. -->
      <div class="review-prompt__header">
        <svg
          class="review-prompt__icon"
          width="30"
          height="30"
          viewBox="0 0 163.599 143.802"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M 163.433 143.381 C 159.752 146.926 128.891 119.293 83.878 118.501 C 36.211 117.652 4 147.677 0.157 143.381 C -3.686 139.084 64.324 25.889 67.156 25.898 C 69.988 25.908 88.725 61.797 90.897 61.486 C 93.069 61.176 101.757 43.18 105.093 41.811 C 108.43 40.441 167.114 139.836 163.433 143.381 Z M 105.136 16.252 C 100.471 16.252 96.69 20 96.69 24.625 C 96.69 29.25 100.471 32.999 105.136 32.999 C 109.802 32.999 113.582 29.25 113.582 24.625 C 113.582 20 109.802 16.252 105.136 16.252 Z M 67.071 0 C 62.405 0 58.625 3.748 58.625 8.373 C 58.625 12.999 62.405 16.747 67.071 16.747 C 71.736 16.747 75.517 12.999 75.517 8.373 C 75.517 3.748 71.736 0 67.071 0 Z M 117.535 72.999 C 117.215 71.659 107.166 52.199 105.15 52.037 C 103.135 51.875 97.057 63.239 97.232 65.276 C 97.407 67.313 99.906 73.769 101.213 73.593 C 102.519 73.416 104.676 67.205 105.921 67.313 C 107.166 67.42 112.902 78.88 114.681 78.685 C 116.46 78.49 117.854 74.339 117.535 72.999 Z M 78.342 55.46 C 78.354 52.66 68.595 35.594 67.171 35.403 C 65.746 35.213 55.58 52.376 55.814 55.516 C 56.048 58.656 58.067 60.735 59.381 60.849 C 60.695 60.962 62.991 54.491 64.631 54.498 C 66.271 54.505 71.384 64.918 72.778 64.555 C 74.171 64.191 78.33 58.26 78.342 55.46 Z"
            fill="currentColor"
            fill-rule="nonzero"
          />
        </svg>
        <div class="review-prompt__context">{{ contextLabel }}</div>
      </div>

      <div class="review-prompt__body">
        <div class="review-prompt__copy">
          <h2 class="review-prompt__heading">{{ heading }}</h2>
          <p class="review-prompt__sub">{{ subCopy }}</p>
        </div>

        <!-- Step 1: neither choice is visually dominant, so the positive answer isn't
             pre-weighted. -->
        <div v-if="step === 'ask'" class="review-prompt__choices">
          <button type="button" class="review-prompt__choice" @click="choose('good')">It did</button>
          <button type="button" class="review-prompt__choice" @click="choose('bad')">Not really</button>
        </div>

        <a
          v-if="step === 'good'"
          class="review-prompt__cta"
          :href="reviewUrl"
          target="_blank"
          rel="noopener"
          @click="onReviewClick"
        >Leave a Trustpilot review</a>

        <template v-if="step === 'bad'">
          <textarea
            ref="feedback"
            v-model="feedbackBody"
            class="review-prompt__textarea"
            rows="4"
            placeholder="The route, the map, the recording…"
          />
          <button
            type="button"
            class="review-prompt__cta review-prompt__cta--button"
            :disabled="feedbackBody.trim().length === 0 || submitting"
            @click="submitFeedback"
          >{{ submitting ? 'Sending…' : 'Send it' }}</button>
        </template>

        <button type="button" class="review-prompt__text-button" @click="dismiss">
          {{ dismissLabel }}
        </button>

        <!-- Not in the original spec. The handoff's own compliance note says the review path must
             not be withheld from unhappy users, but step 2b as drawn had no route to it — which is
             the review gating Trustpilot prohibits. This is the smallest fix that keeps the
             sentiment routing: low-emphasis, never competing with "Send it". -->
        <a
          v-if="step === 'bad'"
          class="review-prompt__text-button review-prompt__text-link"
          :href="reviewUrl"
          target="_blank"
          rel="noopener"
          @click="onReviewClick"
        >Or leave a public review</a>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

type Step = 'ask' | 'good' | 'bad';

const COPY: Record<Step, { heading: string; sub: string; dismiss: string }> = {
  ask: {
    heading: 'Did HiiKER get you where you were going?',
    sub: 'We ask once, after a hike. Then we leave you alone.',
    dismiss: 'Not now'
  },
  good: {
    heading: 'Then tell one other hiker.',
    sub: 'A Trustpilot review is the main way people decide whether to trust an app with their navigation. Two sentences is enough.',
    dismiss: 'Remind me after my next hike'
  },
  bad: {
    heading: 'What went wrong?',
    sub: 'Goes to the team, not to a public page.',
    dismiss: 'No thanks'
  }
};

export default defineComponent({
  name: 'ReviewPrompt',
  props: {
    contextLabel: { type: String, default: 'A quick question' },
    reviewUrl: { type: String, required: true },
    dismissUrl: { type: String, required: true },
    feedbackUrl: { type: String, required: true }
  },
  data() {
    return {
      isOpen: true,
      step: 'ask' as Step,
      feedbackBody: '',
      submitting: false,
      // Returned to on close, per the handoff's focus requirement.
      previouslyFocused: null as HTMLElement | null
    };
  },
  computed: {
    heading(): string {
      return COPY[this.step].heading;
    },
    subCopy(): string {
      return COPY[this.step].sub;
    },
    dismissLabel(): string {
      return COPY[this.step].dismiss;
    }
  },
  mounted() {
    this.previouslyFocused = document.activeElement as HTMLElement | null;
    this.focusModal();
  },
  methods: {
    choose(step: Step) {
      this.step = step;
      // The textarea is the point of step 2b, so land the caret in it.
      this.$nextTick(() => (step === 'bad' ? (this.$refs.feedback as HTMLElement)?.focus() : this.focusModal()));
    },

    focusModal() {
      this.$nextTick(() => (this.$refs.modal as HTMLElement)?.focus?.());
    },

    onKeydown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        this.dismiss();
        return;
      }
      if (event.key !== 'Tab') return;

      // Focus trap. Without it, tabbing walks the page behind a modal that covers it.
      const focusable = Array.from(
        (this.$refs.modal as HTMLElement).querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], textarea'
        )
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },

    // Every close path is a dismissal: "Not now", "No thanks", "Remind me…", the scrim and Esc.
    // The server decides what a dismissal costs (90 days, or permanent on the second).
    dismiss() {
      this.post(this.dismissUrl, {});
      this.close();
    },

    // Clicking through to review is not a dismissal — /review records that separately and stops
    // asking permanently — so this only closes the modal.
    onReviewClick() {
      this.close();
    },

    async submitFeedback() {
      if (this.feedbackBody.trim().length === 0 || this.submitting) return;

      this.submitting = true;
      await this.post(this.feedbackUrl, { body: this.feedbackBody.trim() });
      this.close();
    },

    close() {
      this.isOpen = false;
      this.previouslyFocused?.focus?.();
    },

    // Fire-and-forget: a prompt must never leave somebody stuck behind a modal because a POST
    // failed. The server is the source of truth for suppression either way.
    post(url: string, body: Record<string, unknown>): Promise<void> {
      const meta = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null;

      return fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          ...(meta?.content ? { 'X-CSRF-Token': meta.content } : {})
        },
        body: JSON.stringify(body),
        keepalive: true
      })
        .then(() => undefined)
        .catch(() => undefined);
    }
  }
});
</script>

<style scoped>
/* Values are from the HiiKER design system via the handoff. Scoped rather than tokenised because
   the surrounding pages are Bootstrap-themed and light in places — this modal is always dark. */
.review-prompt__scrim {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
}

.review-prompt {
  width: 460px;
  max-width: calc(100vw - 32px);
  background: #22242a;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 34px rgb(0, 0, 0);
  display: flex;
  flex-direction: column;
  font-family: 'Montserrat', sans-serif;
  outline: none;
}

.review-prompt__header {
  background: #fddf00;
  padding: 22px 32px;
  display: flex;
  align-items: center;
  gap: 14px;
}

.review-prompt__icon { display: block; color: #17181d; }

.review-prompt__context {
  font-size: 13px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #17181d;
}

.review-prompt__body {
  padding: 30px 32px 26px;
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.review-prompt__copy { display: flex; flex-direction: column; gap: 10px; }

.review-prompt__heading {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.02em;
  color: #fff;
  text-wrap: pretty;
}

.review-prompt__sub {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.55;
  letter-spacing: 0.02em;
  color: #acb0bf;
  text-wrap: pretty;
}

.review-prompt__choices { display: flex; gap: 10px; }

.review-prompt__choice {
  flex: 1;
  background: #2d2f35;
  border: 1px solid #434343;
  border-radius: 8px;
  padding: 18px 12px;
  font-family: inherit;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #fff;
  cursor: pointer;
  transition: all 140ms ease;
}

.review-prompt__choice:hover,
.review-prompt__choice:focus-visible {
  border-color: #fddf00;
  background: #33363d;
}

.review-prompt__cta {
  display: block;
  background: #fddf00;
  color: #17181d;
  border: none;
  border-radius: 100px;
  padding: 16px 24px;
  font-family: inherit;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  transition: background 140ms ease;
}

.review-prompt__cta:hover { background: #ffd000; color: #17181d; }

.review-prompt__cta--button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.review-prompt__textarea {
  background: #2d2f35;
  border: 1px solid #505259;
  border-radius: 8px;
  color: #fff;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.55;
  letter-spacing: 0.02em;
  padding: 14px;
  resize: none;
  outline: none;
}

.review-prompt__textarea:focus { border-color: #fddf00; }

.review-prompt__text-button {
  background: none;
  border: none;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #717171;
  cursor: pointer;
  padding: 0;
  align-self: center;
  text-decoration: none;
  transition: color 140ms ease;
}

.review-prompt__text-button:hover { color: #acb0bf; }

/* The compliance link sits under the dismiss control and is never the loudest thing on screen. */
.review-prompt__text-link { margin-top: -12px; }

/* The two choice buttons stay side by side — the labels are short. */
@media (max-width: 500px) {
  .review-prompt__body { padding: 24px 20px 20px; }
  .review-prompt__header { padding: 18px 20px; }
  .review-prompt__heading { font-size: 21px; }
}
</style>
